import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { TaskService } from 'lendio-borrower/models/task/task.service';
import {
  CompleteTask,
  CreateTask, GetIssueTypes,
  GetTasksForBorrower,
  ReOpenTask,
  TaskCreated,
  UpdateTaskContractRequest
} from './workflow-tasks.actions';
import { Task } from 'lendio-borrower/models/task/task.model';
import { tap } from 'rxjs/operators';
import { TaskStore } from 'lendio-borrower/models/task/task.store';
import { patch, updateItem } from '@ngxs/store/operators';
import {IssueTypeCategory} from 'ng2-app/modules/task-management/models/workflow-task/issue-types.model';
import { SendToFundingDesk } from 'app/modules/today/state/user-action-items.action';

export class WorkflowTasksStateModel {
  tasks: Array<Task>;
  issueTypeCategories: IssueTypeCategory[];
}

@State<WorkflowTasksStateModel>({
  name: 'workflowTasks',
  defaults: {
    tasks: [],
    issueTypeCategories: [],
  }
})

@Injectable()
export class WorkflowTasksState {

  @Selector()
  static tasks(state: WorkflowTasksStateModel) {
    return state.tasks;
  }

  @Selector()
  static completedTasks(state: WorkflowTasksStateModel) {
    return state.tasks.filter( task => task.completed );
  }

  @Selector()
  static incompleteTasks(state: WorkflowTasksStateModel) {
    return state.tasks.filter( task => !task.completed );
  }

  @Selector()
  static incompleteKickbacks(state: WorkflowTasksStateModel) {
    let kickbackCount: Task[] = [];
    kickbackCount = state.tasks.filter( task => {
      return (task.type?.toLowerCase() === 'kickback') && (!task.completed);
    });
    return kickbackCount.length > 0 ?? false;
  }

  @Selector()
  static issueTypeCategories(state: WorkflowTasksStateModel) {
    return state.issueTypeCategories;
  }

  constructor(
    private store: Store,
    private tasksService: TaskService,
    private taskStore: TaskStore,
  ) {}

  @Action(GetIssueTypes)
  getIssueTypes(
    { patchState }: StateContext<WorkflowTasksStateModel>
  ) {
    return this.tasksService.getIssueTypes()
      .subscribe( issueTypeCategories => {
        patchState({issueTypeCategories});
      });
  }

  @Action(GetTasksForBorrower)
  getTasksForBorrower({ patchState }: StateContext<WorkflowTasksStateModel>, { borrowerId }: GetTasksForBorrower) {
    return this.tasksService.tasksForBorrower(borrowerId)
      .pipe(
        tap( tasks => {
          patchState({ tasks });
        })
      );
  }

  @Action(CreateTask)
  createTask({ getState, patchState }: StateContext<WorkflowTasksStateModel>, { task }: CreateTask) {
    return this.taskStore.save(task).subscribe({
      next: (newTask) => {
        const tasks = getState().tasks;
        tasks.unshift(newTask);
        patchState({tasks});
      },
      error: (error) => {

      }
    });
  }

  @Action(UpdateTaskContractRequest)
  updateTaskContractRequest({ setState }: StateContext<WorkflowTasksStateModel>, { updatedTask }: UpdateTaskContractRequest) {
    setState(
      patch({
        tasks: updateItem( (task: Task) => task.id === updatedTask.id, patch({
          contract_request: updatedTask.contract_request
        }))
      })
    );
  }

  @Action(TaskCreated)
  taskCreated({ getState, patchState }: StateContext<WorkflowTasksStateModel>, { task }: TaskCreated) {
    const tasks = getState().tasks;
    tasks.unshift(task);
    patchState({tasks});
  }

  @Action(CompleteTask)
  completeTask({ getState, setState }: StateContext<WorkflowTasksStateModel>, { updatedTask, borrowerName }: CompleteTask) {
    setState(
      patch({
        tasks: updateItem( (task: Task) => task.id === updatedTask.id, patch({
          completed: updatedTask.completed,
        }))
      })
    );
    return this.taskStore.complete(updatedTask).subscribe( (completedTask: Task) => {
      setState(
        patch({
          tasks: updateItem( (task: Task) => task.id === completedTask.id, patch({
            completed: completedTask.completed,
          }))
        })
      );
      const remainingTasks = getState().tasks.filter(tasks => tasks.id !== completedTask.id);

      const additionalKickbacks = this.multipleExistingKickbacksForBorrower(remainingTasks, completedTask.borrowerId);
      if (!additionalKickbacks && updatedTask.type?.toLowerCase() === 'kickback') {
        const borrower = {
          borrowerId: completedTask.borrowerId,
          borrowerName: borrowerName ?? 'this borrower'
        };
        this.store.dispatch(new SendToFundingDesk(borrower));
      }
    });
  }

  @Action(ReOpenTask)
  reOpenTask({ setState }: StateContext<WorkflowTasksStateModel>, { updatedTask }: ReOpenTask) {
    return this.taskStore.reOpen(updatedTask).subscribe( (reopenedTask: Task) => {
      setState(
        patch({
          tasks: updateItem( (task: Task) => task.id === reopenedTask.id, patch({
            completed: reopenedTask.completed,
          }))
        })
      );
    });
  }

  /**
   * Checks for additional kickback requests for the borrowerId argument.
   * Determiner for auto re-submission of the borrower application to Funding Desk.
   * Replica of user-action-items.state.ts multipleExistingKickbacksForBorrower()
   * 
   * @param remainingTasks 
   * @param borrowerId 
   * @returns boolean
   */
  private multipleExistingKickbacksForBorrower(remainingTasks: Task[], borrowerId: number): boolean {
    return remainingTasks.some((rt) => {
      return (rt.type?.toLowerCase() === 'kickback') && (rt.borrowerId === borrowerId) && (!rt.completed);
    });
  }

}
